import ConversationDetails from 'components/Conversation/ConversationDetails';
import React, { useEffect, useState } from 'react';
import TransferConversationModal from './components/notificationModal/transferModal';
import WhisperModal from './components/notificationModal/whisperModal';

// react virtualized
import { AutoSizer, Column, Table } from 'react-virtualized';
import 'react-virtualized/styles.css';

import { verifyRoles } from 'components/Authentication/VerifyRoles';
import { IconsChannel } from 'components/IconsChannel/IconsChannel';
import { TagChatClient } from 'components/TagChatClient';
import { toast } from 'react-toastify';
import { Input } from 'reactstrap';
import ConversationReportService from 'views/ChatConfiguration/service';
import { individual_column_style } from '../../../../../../assets/styles/table_custom_style';
import { ChatSituations } from '../../../../../../utils/fuctions/mapSituationChat';
import CloseSingleButton from '../CloseSingleButton';

const reactVirtualizedTableStyle = {
  ...individual_column_style,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  minWidth: '150px',
  fontSize: '.8125rem'
};

const reactVirtualizedTableHeaderStyle = {
  // individual_column_style
  color: '#8898aa',
  backgroundColor: '#f6f9fc',
  fontWeight: '600',
  paddingTop: '0.75rem',
  paddingBottom: '0.75rem',
  fontSize: '.65rem',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  borderBottom: '1px solid #e9ecef',
  borderTop: '1px solid #e9ecef',
  paddingLeft: '1.5rem',
  paddingRight: '0',
  whiteSpace: 'break-spaces',
  cursor: 'pointer',
  lineHeight: '1.5rem',
  height: '56.5px',
  marginRight: '0px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start'
  // ...individual_header_curst_style
};

/**
 * @typedef NameTag
 * @property {id} id
 * @property {string} name
 * @property {boolean} disabled
 * @property {string} color - Uma cor em formato HEX, incluindo #
 */

/**
 * Representa um cliente.
 * @typedef {Object} Client
 * @property {string} id - O ID do cliente
 * @property {string} nome - O nome do cliente
 * @property {string|null} email - O email do cliente
 * @property {string} telefone - O telefone do cliente
 * @property {string} id_postgres - O ID do cliente no banco de dados Postgres
 * @property {string} id_facebook - O ID do cliente no Facebook
 * @property {string} id_telegram - O ID do cliente no Telegram
 * @property {string} id_whatsapp - O ID do cliente no WhatsApp
 * @property {string|null} id_instagram - O ID do cliente no Instagram
 * @property {string} id_zenvia_sms - O ID do cliente no Zenvia SMS
 * @property {boolean} was_created_automatically - Indica se o cliente foi criado automaticamente
 * @property {number} record_type_id - O ID do tipo de registro do cliente
 * @property {Array|null} broadcastList - A lista de transmissão do cliente
 * @property {Array} tags - As tags do cliente
 * @property {string} created_at - A data de criação do cliente
 * @property {string} last_updated_at - A data da última atualização do cliente
 * @property {string} created_by - Quem criou o cliente
 * @property {string} last_updated_by - Quem atualizou o cliente pela última vez
 * @property {number|null} latitude - A latitude do cliente
 * @property {number|null} longitude - A longitude do cliente
 */

/**
 * Representa um atendente.
 * @typedef {Object} Attendant
 * @property {string} id_postgres - O ID do atendente no banco de dados Postgres
 * @property {string} name - O nome do atendente
 */

/**
 * Representa conversas entre cliente e atendente. É utilizado no atendimento e no monitoramento.
 * @typedef {Object} Conversation
 * @property {string} _id - O ID da conversa
 * @property {string} canal - O canal da conversa (e.g., whatsapp)
 * @property {string} apelidoCanal - O apelido do canal
 * @property {boolean} status - Indica se a conversa foi atendida
 * @property {boolean} atendimentoBot - Indica se o atendimento foi realizado por um bot
 * @property {string} situacao - A situação da conversa (e.g., em_atendimento)
 * @property {string} fila - A fila da conversa (e.g., TELEVENDAS)
 * @property {Client} cliente - Informações do cliente
 * @property {string} hora_criacao - A hora de criação da conversa
 * @property {Attendant} atendente - Informações do atendente
 * @property {string} hora_do_atendimento - A hora do atendimento
 * @property {boolean} isBroadCastList - Indica se é uma lista de transmissão
 * @property {string} nameBroadCastList - O nome da lista de transmissão
 * @property {Array} tags - As tags da conversa
 */

/**
 * Um modal para fechar conversa. É possível escolher entre criar ou não protocolo.
 * @param {Object} props
 * @param {string} props.title - O texto que aparecerá no header do cabeçalho
 * @param {string} props.tooltipTitle - O texto que aparecerá no tooltip antes do modal abrir
 * @param {string} props.chatId - O ID do chat.
 * @param {string} props.reason - O motivo em forma de string
 * @param {string} props.subReason - O sub-motivo em forma de string
 * @param {string} props.idProtocol - O id do protocolo em forma de UUID
 * @param {string} props.recordType - O tipo de registro do protocolo
 * @param {boolean} props.shouldSkipProtocolCreationOnTheBackend - Indica se a criação de protocolo deve ser PULADA ou não. É utilizado puramente no back-end. Não exerce influência alguma no front-end. Para esse uso, utilize `shouldShowProtocolAtTheEndOfConversation`.
 * @param {boolean} props.shouldShowProtocolAtTheEndOfConversation - Indica se um protocolo deve ou não ser criado ao encerrar a conversa
 * @param {Conversation} props.conversation - A conversa associada ao modal
 * @returns {React.ReactElement}
 */

/**
 * Renderiza a tabela do monitoramento de chats
 * @param {Object} props
 * @param {Conversation[]} props.data - Uma lista de conversas
 * @param {boolean} props.isCurrentAgentsLogged
 * @param {Conversation[]} props.selectedRows - A lista de conversas que estão selecionadas
 * @param {NameTag[]} props.idNameTags
 */
const TableMonitoring = ({
  data,
  isCurrentAgentsLogged,
  selectedRows,
  handleOnSelect,
  handleOnSelectAll,
  handleOnSortChange,
  idNameTags
}) => {
  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} - Determina se deve ou não ser criado um protocolo ao encerrar a conversa */
  const [
    shouldCreateProtocolOnConversationEnd,
    setShouldCreateProtocolOnConversationEnd
  ] = useState(false);

  useEffect(() => {
    ConversationReportService.getConfigProtocolChat()
      .then((response) => {
        const { criarProtocoloParaConversaNoMonitoramento } = response.data;
        setShouldCreateProtocolOnConversationEnd(
          criarProtocoloParaConversaNoMonitoramento
        );
      })
      .catch((error) => {
        if (error.response) {
          toast.error('Erro ao carregar configurações!', {
            autoClose: 3000,
            closeOnClick: true
          });
        }
        return false;
      });
  }, []);

  return (
    <div
      style={{
        overflowX: 'auto',
        overflowY: 'hidden',
        height: 'calc(100vh - 200px)'
      }}
    >
      <div
        style={{
          width: '1820px',
          height: '100%'
        }}
      >
        {
          <AutoSizer>
            {({ width, height }) => {
              return (
                <Table
                  width={width}
                  height={height}
                  headerHeight={57}
                  rowHeight={57}
                  style={{
                    height: '100%',
                    padding: '0 1rem 0 1rem'
                  }}
                  rowStyle={{
                    fontSize: '.8125rem',
                    borderBottom: '1px solid #e9ecef'
                  }}
                  headerStyle={{
                    ...reactVirtualizedTableHeaderStyle
                  }}
                  rowCount={data.length || 0}
                  rowGetter={({ index }) => data[index] || {}}
                  onHeaderClick={(event) => {
                    const { dataKey } = event;
                    if (dataKey === 'checkbox') return;
                    handleOnSortChange(dataKey);
                  }}
                >
                  <Column
                    label="Selecionar"
                    dataKey="checkbox"
                    width={30}
                    style={{
                      padding: '1.5rem 1.5rem',
                      marginBottom: '1.5rem'
                    }}
                    headerRenderer={() => {
                      return (
                        <Input
                          type="checkbox"
                          style={{
                            padding: '1.5rem 1.5rem',
                            marginLeft: '-0.5rem',
                            position: 'static'
                          }}
                          checked={selectedRows.length === data.length}
                          onChange={(event) => {
                            handleOnSelectAll(event.target.checked, data);
                          }}
                        />
                      );
                    }}
                    cellRenderer={({ rowData }) => {
                      return (
                        <Input
                          type="checkbox"
                          name={rowData._id}
                          checked={selectedRows.some(
                            (x) => x._id === rowData._id
                          )}
                          onChange={(event) => {
                            handleOnSelect(rowData, event.target.checked);
                          }}
                        />
                      );
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />

                  <Column
                    label="Cliente"
                    cellDataGetter={({ rowData }) => {
                      return rowData?.cliente?.nome;
                    }}
                    dataKey="cliente"
                    width={160}
                    style={{
                      marginRight: '0px'
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Tags"
                    cellRenderer={({ rowData }) => {
                      const countTags = rowData?.tags?.length;

                      if (rowData?.tags && rowData.tags.length > 2)
                        return (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'flex-start'
                            }}
                          >
                            <TagChatClient
                              name={idNameTags[rowData.tags[0]]?.name}
                              color={
                                idNameTags[rowData.tags[0]]?.color || '#5086c1'
                              }
                              disabled={idNameTags[rowData.tags[0]]?.disabled}
                            />
                            <TagChatClient
                              name={idNameTags[rowData.tags[2]]?.name}
                              color={
                                idNameTags[rowData.tags[2]]?.color || '#5086c1'
                              }
                              disabled={idNameTags[rowData.tags[1]]?.disabled}
                            />
                            <TagChatClient
                              name={`+${countTags - 2}`}
                              allTags={rowData?.tags}
                              idNameTags={idNameTags}
                              isMore={true}
                              color="#5086c1"
                              index={rowData?.id || rowData?._id}
                            />
                          </div>
                        );
                      else
                        return (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'flex-start'
                            }}
                          >
                            {Object.keys(idNameTags).length > 0 &&
                              rowData?.tags &&
                              rowData?.tags?.map((tag, index) => (
                                <TagChatClient
                                  key={index}
                                  name={idNameTags[tag]?.name}
                                  color={idNameTags[tag]?.color || '#5086c1'}
                                  disabled={idNameTags[tag]?.disabled}
                                  sizeText="auto"
                                />
                              ))}
                          </div>
                        );
                    }}
                    dataKey="tags"
                    width={180}
                    style={{
                      marginRight: '0px',
                      paddingLeft: '5px'
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Telefone"
                    dataKey="telefone"
                    cellDataGetter={({ rowData }) => {
                      return rowData?.cliente?.telefone;
                    }}
                    width={140}
                    style={{
                      marginRight: '0px'
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Atendente"
                    dataKey="atendente"
                    cellDataGetter={({ rowData }) => {
                      return rowData?.atendente?.name;
                    }}
                    style={{
                      marginRight: '0px'
                    }}
                    width={150}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Status"
                    dataKey="atendida"
                    cellDataGetter={({ rowData }) => {
                      return rowData?.atendida ? 'Atendida' : 'Não Atendida';
                    }}
                    style={{
                      marginRight: '0px'
                    }}
                    width={150}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Fila"
                    dataKey="fila"
                    width={150}
                    style={{
                      marginRight: '0px'
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Nome do Canal"
                    dataKey="apelidoCanal"
                    width={150}
                    style={{
                      marginRight: '0px'
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Canal"
                    dataKey="canal"
                    width={180}
                    cellRenderer={({ rowData }) => {
                      return <IconsChannel channel={rowData.canal} />;
                    }}
                    style={{
                      display: 'flex',
                      marginRight: '0px',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    headerStyle={{
                      display: 'flex',
                      marginRight: '0px',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle,
                      paddingLeft: '5px'
                    }}
                  />
                  <Column
                    label="Data e Hora"
                    dataKey="hora"
                    cellDataGetter={(rowData) => {
                      const date = new Date(rowData?.rowData?.hora_criacao);
                      return date.toLocaleDateString('pt-br') === 'Invalid Date'
                        ? ''
                        : `${date.toLocaleDateString(
                            'pt-br'
                          )} ${date.toLocaleTimeString('pt-br')}`;
                    }}
                    width={150}
                    style={{
                      marginRight: '0px'
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Situação"
                    dataKey="situacao"
                    cellDataGetter={(rowData) => {
                      switch (rowData?.rowData?.situacao) {
                        case ChatSituations.IN_ATTENDANCE:
                          return 'Em atendimento';
                        case ChatSituations.NOT_ANSWERED:
                          return 'Não atendida';
                        case ChatSituations.TRANSFERRED:
                          return 'Transferida';
                        case ChatSituations.WAITING_IN_QUEUE:
                          return 'Aguardando fila';
                        case ChatSituations.ASSIGNED:
                          return 'Atribuída';
                        default:
                          return 'Tipo inválido';
                      }
                    }}
                    width={150}
                    style={{
                      marginRight: '0px'
                    }}
                    rowStyle={{
                      ...reactVirtualizedTableStyle
                    }}
                  />
                  <Column
                    label="Ações"
                    cellRenderer={({ rowData }) => {
                      return (
                        <>
                          <ConversationDetails
                            conversationData={rowData}
                            getDataOnModalOpen={true}
                            id={rowData._id ?? rowData.id}
                          />
                          {verifyRoles(['CHAT_TRANSFERS']) && (
                            <TransferConversationModal
                              id={rowData._id}
                              name="Transferir conversa"
                              tooltipTitle="Transferir conversa"
                              conversation={rowData}
                              isCurrentAgentsLogged={isCurrentAgentsLogged}
                            ></TransferConversationModal>
                          )}
                          {verifyRoles(['WHISPER_TOOL']) && (
                            <WhisperModal
                              id={rowData._id}
                              name="Sussurrar conversa"
                              tooltipTitle="Sussurrar conversa"
                            ></WhisperModal>
                          )}
                          {/* MONITORAMENTO */}
                          <CloseSingleButton
                            conversation={rowData}
                            shouldCreateProtocolOnConversationEnd={
                              shouldCreateProtocolOnConversationEnd
                            }
                          />
                        </>
                      );
                    }}
                    width={150}
                    minwidth={150}
                    flexGrow={1}
                    rowStyle={{
                      ...reactVirtualizedTableStyle,
                      display: 'flex',
                      width: '100%',
                      minWidth: '150px',
                      overflow: 'auto'
                    }}
                  />
                </Table>
              );
            }}
          </AutoSizer>
        }
      </div>
    </div>
  );
};
export default TableMonitoring;
